"use client";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PiDotsThreeOutlineVerticalFill, PiPencilSimple, PiPause, } from "react-icons/pi";
import { ActionIcon, Button, Box, Popover, Select, Textarea } from "rizzui";
import { updateUserKyc } from "../../app/api/patch-kyc";

const KYC_OPTIONS = [
  "Success",
  "Pending",
  "Failed",
  "YetToStart",
  "Issued",
] as const;

export function MoreActions({
  id,
  currentStatus,
  onStatusUpdated,
  onSuspend,
  currentSuspendStatus
}: {
  id: string;
  currentStatus: (typeof KYC_OPTIONS)[number];
  currentSuspendStatus: boolean
  onStatusUpdated: (
    newStatus: "Success" | "Pending" | "Failed" | "YetToStart" | "Issued"
  ) => void;
  onSuspend: (suspendReason: string) => void;
}) {
  const navigate = useNavigate();

  const [editOpen, setEditOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<
    "Success" | "Pending" | "Failed" | "YetToStart" | "Issued"
  >(currentStatus);

  const [confirmOpen, setConfirmOpen] = useState(false);

    const [suspendOpen, setSuspendOpen] = useState(false);
    const [suspendReason, setSuspendReason] = useState("");

      const suspendReasons = [
        "Invalid verification document",
        "Money fraud",
        "Suspicious activity",
        "Spam campaign",
        "Inappropriate content",
      ];


  const openEdit = () => {
    setNewStatus(currentStatus);
    setEditOpen(true);
  };

  const handleChangeClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    try {
      await updateUserKyc(id, newStatus);
      onStatusUpdated(newStatus);
    } catch (e) {
      console.error("Failed to patch KYC status", e);
    } finally {
      setConfirmOpen(false);
      setEditOpen(false);
    }
  };

  return (
    <>
      <Popover placement="bottom-end">
        <Popover.Trigger>
          <ActionIcon title="More Options" variant="text">
            <PiDotsThreeOutlineVerticalFill className="h-5 w-5 text-gray-600" />
          </ActionIcon>
        </Popover.Trigger>

        <Popover.Content className=" w-[160px] p-2 bg-white shadow rounded">
          {({ setOpen }) => (
            <Box>
              <Button
                variant="text"
                className="w-full justify-start px-2 py-1 hover:bg-gray-100"
                onClick={() => {
                  navigate(`/user-management/user-details/${id}`);
                  setOpen(false);
                }}
              >
                <PiPencilSimple className="mr-2" />
                View User Info
              </Button>

              <Button
                variant="text"
                className="w-full justify-start px-2 py-1 hover:bg-gray-100"
                onClick={() => {
                  setOpen(false);
                  openEdit();
                }}
              >
                <PiPencilSimple className="mr-2" />
                Edit KYC Status
              </Button>
                
                {/* Suspend */}
                <Button
                  variant="text"
                  className="flex w-full px-2 justify-start py-2 hover:bg-gray-100"
                  onClick={() => {
                    setOpen(false);
                    setSuspendOpen(true);
                  }}
                >
                  <PiPause className="mr-2 h-5 w-5 text-gray-500" />
                  {currentSuspendStatus ? "Unsuspend" : "Suspend"}
                </Button>
            </Box>
          )}
        </Popover.Content>
      </Popover>

      {/* Edit KYC Status Dialog */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Box className="bg-white p-6 rounded shadow-lg w-full max-w-sm">
            <h3 className="text-lg font-medium mb-4">Edit KYC Status</h3>
            <p className="mb-2 text-sm text-gray-700">
              Current status: <strong>{currentStatus}</strong>
            </p>
            <Select
              options={KYC_OPTIONS.map((s) => ({ label: s, value: s }))}
              value={newStatus}
              onChange={(val: {
                label:
                  | "Success"
                  | "Pending"
                  | "Failed"
                  | "YetToStart"
                  | "Issued";
                value:
                  | "Success"
                  | "Pending"
                  | "Failed"
                  | "YetToStart"
                  | "Issued";
              }) => {
                setNewStatus(val.value);
              }}
              className="mb-4 w-full"
              // ensure the dropdown is above the modal
              dropdownClassName="!z-[10000]"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleChangeClick}>Change</Button>
            </div>
          </Box>
        </div>
      )}

      {/* Edit KYC Confirmation Dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Box className="bg-white p-6 rounded shadow-lg w-full max-w-xs">
            <h4 className="text-md font-semibold mb-3">Are you sure?</h4>
            <p className="mb-4 text-sm">
              Change KYC status to <strong>{newStatus}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                No
              </Button>
              <Button color="danger" onClick={handleConfirm}>
                Yes, confirm
              </Button>
            </div>
          </Box>
        </div>
      )}

      {/* Suspend confirmation dialog */}
            {suspendOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <Box className="bg-white p-6 rounded-md w-full max-w-sm">
                  {!currentSuspendStatus && (
                    <>
                      <h2 className="text-lg font-medium mb-4">
                        Reason for suspending campaign
                      </h2>
        
                      {/* Predefined reasons */}
                      <div className="grid grid-cols-1 gap-2 mb-4">
                        {suspendReasons.map((r) => (
                          <Button
                            key={r}
                            variant={suspendReason === r ? "flat" : "outline"}
                            onClick={() => setSuspendReason(r)}
                          >
                            {r}
                          </Button>
                        ))}
                      </div>
          
                      {/* Free-text override */}
                      <Textarea
                        placeholder="Or enter custom reason…"
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        className="w-full mb-4"
                      />
                  </>
                    
                  )}
                  
      
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSuspendReason("");
                        setSuspendOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="danger"
                      onClick={() => {
                        console.log(
                          `Suspend email: campaign ${id} reason: "${suspendReason}"`
                        );
                        onSuspend(suspendReason.trim() || "No reason provided");
                        setSuspendReason("");
                        setSuspendOpen(false);
                      }}
                    >
                      {currentSuspendStatus ? "Unsuspend" : "Suspend"}
                    </Button>
                  </div>
                </Box>
              </div>
            )}
    </>
  );
}
